import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, MapPin, GraduationCap, TrendingUp, HeartHandshake, Upload, Loader2 } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { IMG, JOBS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const perks = [
  { icon: GraduationCap, title: "Training & Development", body: "Ongoing upskilling and manufacturer certifications." },
  { icon: TrendingUp, title: "Career Progression", body: "Clear pathways from apprentice to lead technician." },
  { icon: HeartHandshake, title: "Supportive Culture", body: "A tight-knit family team that has your back." },
];

const Careers = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: JOBS[0].title, message: "" });
  const [resumeId, setResumeId] = useState(null);
  const [resumeName, setResumeName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd);
      setResumeId(data.id); setResumeName(file.name);
      toast.success("Resume uploaded");
    } catch { toast.error("Upload failed. Please try again."); }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/careers/apply`, { ...form, resume_id: resumeId });
      toast.success("Application submitted — we'll be in touch!");
      setForm({ name: "", email: "", phone: "", position: JOBS[0].title, message: "" }); setResumeId(null); setResumeName("");
    } catch { toast.error("Something went wrong. Please try again."); }
    setSubmitting(false);
  };

  const inp = "w-full h-14 px-5 rounded-2xl bg-js-grey border border-black/10 focus:border-js-blue focus:ring-2 focus:ring-js-blue/20 outline-none transition-colors duration-200";

  return (
    <div data-testid="careers-page">
      <PageHero eyebrow="CAREERS" title="Build your career with J&S" subtitle="Join one of Melbourne's most trusted HVAC teams — where craftsmanship and people come first." image={IMG.tech2} crumbs={[{ label: "Careers" }]} />

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-3 gap-5 mb-16">
            {perks.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="bg-js-grey rounded-3xl p-8 border border-black/5 h-full">
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-js-blue text-white mb-5"><p.icon size={22} /></span>
                  <h3 className="font-display font-bold text-lg mb-2">{p.title}</h3>
                  <p className="text-js-charcoal/60 text-sm leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal><h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mb-8">Current openings</h2></Reveal>
          <div className="space-y-4 mb-20">
            {JOBS.map((j, i) => (
              <Reveal key={j.title} delay={i * 0.04}>
                <div className="flex flex-wrap items-center justify-between gap-4 bg-js-grey rounded-2xl p-6 border border-black/5">
                  <div>
                    <h3 className="font-display font-bold text-lg">{j.title}</h3>
                    {j.tagline && <p className="text-js-blue text-sm font-semibold mt-0.5">{j.tagline}</p>}
                    <p className="text-js-charcoal/60 text-sm mt-1.5">{j.desc}</p>
                    <div className="flex gap-4 mt-3 text-xs text-js-charcoal/50">
                      <span className="flex items-center gap-1.5"><Briefcase size={13} className="text-js-blue" /> {j.type}</span>
                      <span className="flex items-center gap-1.5"><MapPin size={13} className="text-js-blue" /> {j.location}</span>
                    </div>
                  </div>
                  <a href="#apply" onClick={() => setForm((f) => ({ ...f, position: j.title }))} className="px-5 py-2.5 rounded-full bg-js-blue text-white text-sm font-semibold hover:bg-js-dark transition-colors duration-300">Apply</a>
                </div>
              </Reveal>
            ))}
          </div>

          <div id="apply" className="max-w-2xl mx-auto bg-js-charcoal rounded-3xl p-8 md:p-12">
            <h2 className="font-display font-extrabold text-white text-3xl tracking-tight mb-2">Apply now</h2>
            <p className="text-white/60 mb-8">Send us your details and resume — we'd love to hear from you.</p>
            <form onSubmit={submit} className="space-y-4" data-testid="careers-form">
              <input required placeholder="Full name" data-testid="career-name" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input required type="email" placeholder="Email" data-testid="career-email" className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input required placeholder="Phone" data-testid="career-phone" className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <select data-testid="career-position" className={inp} value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}>
                {JOBS.map((j) => <option key={j.title}>{j.title}</option>)}
              </select>
              <textarea placeholder="Tell us about yourself" rows={4} data-testid="career-message" className="w-full p-5 rounded-2xl bg-js-grey border border-black/10 focus:border-js-blue focus:ring-2 focus:ring-js-blue/20 outline-none" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <label data-testid="career-resume" className="flex items-center gap-3 h-14 px-5 rounded-2xl bg-js-grey border border-dashed border-black/20 cursor-pointer hover:border-js-blue transition-colors duration-200">
                {uploading ? <Loader2 size={18} className="animate-spin text-js-blue" /> : <Upload size={18} className="text-js-blue" />}
                <span className="text-sm text-js-charcoal/70">{resumeName || "Upload resume (PDF / DOC)"}</span>
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => upload(e.target.files[0])} />
              </label>
              <button type="submit" disabled={submitting} data-testid="career-submit" className="w-full h-14 rounded-full bg-js-red text-white font-semibold hover:bg-js-red/90 transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60">
                {submitting && <Loader2 size={18} className="animate-spin" />} Submit Application
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
